import { createSelector } from 'reselect'
import moment from 'moment-timezone'
import { activityType } from 'shared/types'

const decisionTypes = {
    [activityType.ADMIN_CONFIRM_BOOKING]: 'accepted',
    [activityType.ADMIN_REJECT_BOOKING]: 'rejected',
}

const selectNotificationGroups = createSelector([
    state => state.site_admin,
    state => state.sites,
    state => state.auth?.user,
    state => state.ui?.applyAdminSiteId || Object.keys(state.auth?.user?.specialPermissionsInSites || {})[0],
    (state, now) => now,
], (siteAdmin, sites, currentUser, currentSiteId, now) => {
    const active = [], past = []
    const site = sites?.find(site => site.id === currentSiteId)
    const permission = currentUser?.specialPermissionsInSites?.[currentSiteId]
    if (!site || !permission?.permissions) return { active, past }
    const canManage = permission.permissions.some(role => role === 'admin' || role === 'front_office')

    const bookingDetails = (booking, historical = false) => {
        if (!booking?.id || !booking.fromDate || !booking.toDate || String(booking.siteId) !== site.id) return null
        if (!canManage && (!permission.permissions.includes('coach') || !booking.supplItemId
            || (permission.supplItemIds && !permission.supplItemIds.includes(String(booking.supplItemId))))) return null
        const user = siteAdmin.users?.[booking.userId] || (historical && { displayName: 'Client no longer available' })
        const item = site.items.find(item => item.id === String(booking.originalItemId || booking.itemId))
            || (historical && { name: 'Facility no longer available' })
        if (!user || !item) return null
        const supplItem = site.supplementaryItems?.find(item => item.id === String(booking.supplItemId))
        return { booking, user, item, site, supplItem }
    }

    for (const booking of siteAdmin?.pending_bookings || []) {
        const data = bookingDetails(booking)
        if (!data) continue
        const startsAt = site.timezone ? moment.tz(booking.fromDate, site.timezone)
            : moment.parseZone(booking.fromDate + site.timezoneOffsetSuffix)
        if (!startsAt.isValid()) continue
        const notification = { id: `pending:${booking.id}`, type: 'pending', sortAt: startsAt.valueOf(), data }
        const group = startsAt.isBefore(now) ? past : active
        group.push(notification)
    }
    active.sort((a, b) => a.data.booking.fromDate.localeCompare(b.data.booking.fromDate))

    for (const activity of siteAdmin?.activity || []) {
        const recordedDecision = decisionTypes[activity.type]
        if (!recordedDecision || !activity.datetime || !moment(activity.datetime).isValid()) continue
        let payload
        try { payload = JSON.parse(activity.data) } catch { continue }
        const data = bookingDetails(payload?.booking, true)
        if (!data) continue
        past.push({ id: `decision:${activity.id}`, type: 'booking_decision', sortAt: moment(activity.datetime).valueOf(),
            data: { ...data, recordedDecision, decidedAt: activity.datetime },
        })
    }
    past.sort((a, b) => b.sortAt - a.sortAt)

    if (canManage) {
        for (const [itemId, sync] of Object.entries(siteAdmin?.sync_calendar_info || {})) {
            if (!sync?.error) continue
            active.push({ id: `sync_calendar:${itemId}`, type: 'sync_calendar', data: {
                itemName: site.items.find(item => item.id === itemId)?.name || 'n/a',
            } })
        }
    }
    return { active, past }
})

// Pass time explicitly to the memoized selector so an unchanged Redux snapshot
// can still move a request into the past when the screen's clock advances.
export const getNotificationGroups = (state, now = Date.now()) => selectNotificationGroups(state, now)
export const getNotifications = (state, now) => getNotificationGroups(state, now).active
